import { useState, useEffect } from 'react';
import useBlockCamera from '../../../hook/block/useBlockCamera';
import { CAMERA_TOP_OFFSET } from '../engine/spawnOrigin';
import { PANEL_EDGE_MARGIN } from '../utils/panelPosition';

// Keep in sync with InsightsPanel.css (drawer width and gap)
const INSIGHTS_PANEL_WIDTH = 360;
const INSIGHTS_PANEL_GAP = 16;

const useInsightsDrawer = ({ panelRef, position, canvasBlocksRef, selectedEntry, selectedEntryId, setSelectedEntryId, pushMessage }) => {
    const { jumpToPosition } = useBlockCamera();
    const [insightsOpen, setInsightsOpen] = useState(false);
    const [insightsTab, setInsightsTab] = useState('usage');
    const [insightsSide, setInsightsSide] = useState('left');
    const [copiedKey, setCopiedKey] = useState(null);

    // jumpToPosition pins a block at screen x=30 and 1/2.5 of the window height, so the target is shifted
    const goToBlock = (blockId) => {
        const block = (canvasBlocksRef.current || []).find((item) => item.object_id === blockId);
        if (!block) return;

        const menuTop = panelRef.current?.getBoundingClientRect().top ?? window.innerHeight * 0.5;
        const targetY = Math.max(80, Math.min(window.innerHeight * 0.4, menuTop - 140));
        const targetX = Math.max(30, window.innerWidth / 2 - 120);

        jumpToPosition(
            block.x + 30 - targetX,
            block.y + CAMERA_TOP_OFFSET + window.innerHeight / 2.5 - targetY,
            () => document.getElementById(blockId)?.classList.add('selected'),
        );
    };

    const computeInsightsSide = () => {
        const rect = panelRef.current?.getBoundingClientRect();
        if (!rect) return 'left';
        const needed = INSIGHTS_PANEL_WIDTH + INSIGHTS_PANEL_GAP + PANEL_EDGE_MARGIN;
        if (rect.left >= needed) return 'left';
        if (window.innerWidth - rect.right >= needed) return 'right';
        return 'over';
    };

    const openInsights = (tab, entryId = null) => {
        setSelectedEntryId(entryId);
        setInsightsTab(tab);
        setInsightsSide(computeInsightsSide());
        setInsightsOpen(true);
    };

    const toggleInsights = () => {
        if (insightsOpen) {
            setInsightsOpen(false);
            return;
        }
        openInsights(insightsTab, selectedEntryId);
    };

    const toggleInsightsForEntry = (entryId) => {
        if (insightsOpen && insightsTab === 'usage' && selectedEntry && selectedEntry.id === entryId) {
            setInsightsOpen(false);
            return;
        }
        openInsights('usage', entryId);
    };

    useEffect(() => {
        if (!insightsOpen) return;

        const updateSide = () => setInsightsSide(computeInsightsSide());
        updateSide();
        window.addEventListener('resize', updateSide);
        return () => window.removeEventListener('resize', updateSide);
    }, [insightsOpen, position]);

    const copyToClipboard = async (key, text) => {
        let copied = false;
        try {
            await navigator.clipboard.writeText(text);
            copied = true;
        } catch (_) {
            try {
                const area = document.createElement('textarea');
                area.value = text;
                area.style.position = 'fixed';
                area.style.opacity = '0';
                document.body.appendChild(area);
                area.select();
                copied = document.execCommand('copy');
                document.body.removeChild(area);
            } catch (__) {
                copied = false;
            }
        }

        if (!copied) {
            pushMessage('error', 'Copy failed: the browser blocked clipboard access.');
            return;
        }
        setCopiedKey(key);
        setTimeout(() => setCopiedKey((current) => current === key ? null : current), 1500);
    };

    return { insightsOpen, setInsightsOpen, insightsTab, setInsightsTab, insightsSide, copiedKey, copyToClipboard, goToBlock, toggleInsights, toggleInsightsForEntry };
};

export default useInsightsDrawer;
